'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { FaEye, FaEyeSlash, FaChevronDown, FaChevronUp } from 'react-icons/fa';

// List of US states for dropdown
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma',
  'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee',
  'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

// List of countries for dropdown
const COUNTRIES = [
  'United States', 'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
  'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh',
  'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina',
  'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
  'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros',
  'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica',
  'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
  'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
  'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hungary',
  'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica', 'Japan',
  'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Korea, North', 'Korea, South', 'Kosovo', 'Kuwait',
  'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania',
  'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands',
  'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro',
  'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand',
  'Nicaragua', 'Niger', 'Nigeria', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine',
  'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar',
  'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
  'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles',
  'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa',
  'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan',
  'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia',
  'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom',
  'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
];

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [filteredStates, setFilteredStates] = useState(US_STATES);
  const [postalCode, setPostalCode] = useState('');
  const [postalCodeError, setPostalCodeError] = useState('');
  const [country, setCountry] = useState('United States');
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [filteredCountries, setFilteredCountries] = useState(COUNTRIES);
  const [telephone, setTelephone] = useState('');
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const stateDropdownRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const router = useRouter();

  // Filter states based on search input
  useEffect(() => {
    if (stateSearch) {
      const filtered = US_STATES.filter(state =>
        state.toLowerCase().includes(stateSearch.toLowerCase())
      );
      setFilteredStates(filtered);
    } else {
      setFilteredStates(US_STATES);
    }
  }, [stateSearch]);

  // Filter countries based on search input
  useEffect(() => {
    if (countrySearch) {
      const filtered = COUNTRIES.filter(country =>
        country.toLowerCase().includes(countrySearch.toLowerCase())
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(COUNTRIES);
    }
  }, [countrySearch]);

  // Clear state and postal code when country changes to non-US
  useEffect(() => {
    if (country !== 'United States') {
      setState('');
      setPostalCode('');
      setPostalCodeError('');
    }
  }, [country]);

  // Handle click outside of dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target)) {
        setShowStateDropdown(false);
      }
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [stateDropdownRef, countryDropdownRef]);

  // Validate postal code
  const validatePostalCode = (code) => {
    // Check if it's a 5-digit or 8-digit (5+3) zip code
    const zipRegex = /^\d{5}(-\d{3})?$/;
    return zipRegex.test(code);
  };

  const handlePostalCodeChange = (e) => {
    const value = e.target.value;
    setPostalCode(value);

    // Only validate postal code for US addresses
    if (country === 'United States') {
      if (value && !validatePostalCode(value)) {
        setPostalCodeError('Please enter a valid 5-digit zip code or 5+3 format (e.g., 12345 or 12345-678)');
      } else {
        setPostalCodeError('');
      }
    } else {
      setPostalCodeError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Form validation
    const isUSAddress = country === 'United States';

    // Basic required fields for all countries
    if (!name || !email || !password || !confirmPassword || !street || !city || !country) {
      setError('Please fill in all required fields');
      return;
    }

    // Telephone/mobile validation only for US addresses
    if (isUSAddress && !telephone && !mobile) {
      setError('Either telephone or mobile is required for US addresses');
      return;
    }

    // Additional validation for US addresses
    if (isUSAddress && (!state || !postalCode)) {
      setError('State and Zip Code are required for US addresses');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    // Validate postal code for US addresses only
    if (isUSAddress && !validatePostalCode(postalCode)) {
      setError('Please enter a valid 5-digit zip code or 5+3 format (e.g., 12345 or 12345-678)');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // For non-US addresses, use "N/A" for state and postalCode
      const isUSAddress = country === 'United States';

      const response = await fetch('/api/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          email,
          password,
          address: {
            street,
            city,
            state: isUSAddress ? state : 'N/A',
            postalCode: isUSAddress ? postalCode : 'N/A',
            country,
          },
          // Only send telephone/mobile for US addresses
          ...(isUSAddress ? { telephone, mobile } : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if this is a rate limit error (status code 429)
        if (response.status === 429) {
          const retryAfter = data.retryAfter || 60; // Default to 60 seconds if not provided
          // Extract failed attempts count if available
          if (data.failedAttempts) {
            setFailedAttempts(data.failedAttempts);
          }
          throw new Error(`Too many registration attempts. Please try again after ${retryAfter} seconds.`);
        }
        throw new Error(data.message || 'Something went wrong');
      }

      setSuccess('Registration successful! Redirecting to login...');

      // Redirect to login page after successful registration
      setTimeout(() => {
        router.push('/login');
      }, 2000);

    } catch (error) {
      setError(error.message || 'An error occurred during registration');
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-lg border border-gray-200" style={{ boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)', background: 'linear-gradient(to right, #f9f9f9, #ffffff, #f9f9f9)', backgroundImage: "url('data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23f0f0f0' fill-opacity='0.2' fill-rule='evenodd'/%3E%3C/svg%3E')" }}>
        <div className="flex justify-center mb-6">
          <Image
            src="/images/Logo.png"
            alt="Logo"
            width={150}
            height={150}
            priority
          />
        </div>
        <div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">Create your account</h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link href="/login" className="font-medium text-blue-600 hover:text-blue-500">
              sign in to your existing account
            </Link>
          </p>
        </div>

        {failedAttempts > 0 && (
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  Warning: You have {failedAttempts} failed registration attempts. Too many failed attempts may result in temporary restrictions.
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-green-700">{success}</p>
              </div>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div className="mb-4">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Full Name"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Password (min. 8 characters)"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? (
                    <FaEyeSlash className="h-5 w-5 text-gray-400" />
                  ) : (
                    <FaEye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Confirm Password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={toggleConfirmPasswordVisibility}
                >
                  {showConfirmPassword ? (
                    <FaEyeSlash className="h-5 w-5 text-gray-400" />
                  ) : (
                    <FaEye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">Address Information</h3>

            <div className="mb-4">
              <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
                Street Address
              </label>
              <input
                id="street"
                name="street"
                type="text"
                required
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Street Address"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  required
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="City"
                />
              </div>

              <div className="mb-4" ref={stateDropdownRef}>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                  State
                  {country !== 'United States' && (
                    <span className="ml-1 text-xs text-gray-500">(US only)</span>
                  )}
                </label>
                <div className="relative">
                  <div
                    className={`appearance-none relative block w-full px-3 py-2 border ${country !== 'United States' ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer'} border-gray-300 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm flex justify-between items-center`}
                    onClick={() => country === 'United States' && setShowStateDropdown(!showStateDropdown)}
                    disabled={country !== 'United States'}
                  >
                    <span className={state ? "text-gray-900" : "text-gray-500"}>
                      {state || (country === 'United States' ? "Select a state" : "N/A for non-US countries")}
                    </span>
                    {country === 'United States' && (showStateDropdown ? <FaChevronUp /> : <FaChevronDown />)}
                  </div>

                  {showStateDropdown && country === 'United States' && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                      <div className="sticky top-0 z-10 bg-white">
                        <input
                          type="text"
                          className="block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="Search states..."
                          value={stateSearch}
                          onChange={(e) => setStateSearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {filteredStates.length > 0 ? (
                        filteredStates.map((stateName) => (
                          <div
                            key={stateName}
                            className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                            onClick={() => {
                              setState(stateName);
                              setShowStateDropdown(false);
                              setStateSearch('');
                            }}
                          >
                            {stateName}
                          </div>
                        ))
                      ) : (
                        <div className="py-2 px-3 text-gray-500">No states found</div>
                      )}
                    </div>
                  )}
                </div>
                {country !== 'United States' && (
                  <p className="mt-1 text-xs text-gray-500">State selection is only available for United States addresses.</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Zip Code
                  {country !== 'United States' && (
                    <span className="ml-1 text-xs text-gray-500">(US only)</span>
                  )}
                </label>
                <input
                  id="postalCode"
                  name="postalCode"
                  type="text"
                  required={country === 'United States'}
                  disabled={country !== 'United States'}
                  value={country === 'United States' ? postalCode : ''}
                  onChange={handlePostalCodeChange}
                  className={`appearance-none relative block w-full px-3 py-2 border ${postalCodeError ? 'border-red-300' : 'border-gray-300'} ${country !== 'United States' ? 'bg-gray-100 cursor-not-allowed' : ''} placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                  placeholder={country === 'United States' ? "5-digit zip code (12345) or 5+3 format (12345-678)" : "N/A for non-US countries"}
                />
                {postalCodeError && country === 'United States' && (
                  <p className="mt-1 text-sm text-red-600">{postalCodeError}</p>
                )}
                {country !== 'United States' && (
                  <p className="mt-1 text-xs text-gray-500">Zip code is only required for United States addresses.</p>
                )}
              </div>

              <div className="mb-4" ref={countryDropdownRef}>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <div className="relative">
                  <div
                    className="appearance-none relative block w-full px-3 py-2 border border-gray-300 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm cursor-pointer flex justify-between items-center"
                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  >
                    <span className={country ? "text-gray-900" : "text-gray-500"}>
                      {country || "Select a country"}
                    </span>
                    {showCountryDropdown ? <FaChevronUp /> : <FaChevronDown />}
                  </div>

                  {showCountryDropdown && (
                    <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                      <div className="sticky top-0 z-10 bg-white">
                        <input
                          type="text"
                          className="block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          placeholder="Search countries..."
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {filteredCountries.length > 0 ? (
                        filteredCountries.map((countryName) => (
                          <div
                            key={countryName}
                            className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                            onClick={() => {
                              setCountry(countryName);
                              setShowCountryDropdown(false);
                              setCountrySearch('');
                            }}
                          >
                            {countryName}
                          </div>
                        ))
                      ) : (
                        <div className="py-2 px-3 text-gray-500">No countries found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <h3 className="text-lg font-medium text-gray-900 mt-6 mb-3">Contact Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-1">
                  Telephone
                  {country !== 'United States' && (
                    <span className="ml-1 text-xs text-gray-500">(US only)</span>
                  )}
                </label>
                <input
                  id="telephone"
                  name="telephone"
                  type="tel"
                  required={country === 'United States' && !mobile}
                  disabled={country !== 'United States'}
                  value={country === 'United States' ? telephone : ''}
                  onChange={(e) => setTelephone(e.target.value)}
                  className={`appearance-none relative block w-full px-3 py-2 border border-gray-300 ${country !== 'United States' ? 'bg-gray-100 cursor-not-allowed' : ''} placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                  placeholder={country === 'United States' ? "(123) 456-7890" : "N/A for non-US countries"}
                  pattern="^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$"
                  title="Please enter a valid phone number in format: (123) 456-7890 or 123-456-7890"
                />
                {country === 'United States' ? (
                  <p className="mt-1 text-xs text-gray-500">Format: (123) 456-7890 or 123-456-7890</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">Telephone is only required for United States addresses.</p>
                )}
              </div>

              <div className="mb-4">
                <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile
                  {country !== 'United States' && (
                    <span className="ml-1 text-xs text-gray-500">(US only)</span>
                  )}
                </label>
                <input
                  id="mobile"
                  name="mobile"
                  type="tel"
                  required={country === 'United States' && !telephone}
                  disabled={country !== 'United States'}
                  value={country === 'United States' ? mobile : ''}
                  onChange={(e) => setMobile(e.target.value)}
                  className={`appearance-none relative block w-full px-3 py-2 border border-gray-300 ${country !== 'United States' ? 'bg-gray-100 cursor-not-allowed' : ''} placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                  placeholder={country === 'United States' ? "(123) 456-7890" : "N/A for non-US countries"}
                  pattern="^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$"
                  title="Please enter a valid phone number in format: (123) 456-7890 or 123-456-7890"
                />
                {country === 'United States' ? (
                  <p className="mt-1 text-xs text-gray-500">Format: (123) 456-7890 or 123-456-7890</p>
                ) : (
                  <p className="mt-1 text-xs text-gray-500">Mobile is only required for United States addresses.</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </div>

          <div className="text-sm text-center">
            <p className="text-gray-600">
              By creating an account, you agree to our{' '}
              <a href="#" className="font-medium text-blue-600 hover:text-blue-500">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="#" className="font-medium text-blue-600 hover:text-blue-500">
                Privacy Policy
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
